import { MaterialIcons } from "@expo/vector-icons";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Box, Divider, HStack, Icon, Pressable, Text, VStack } from "native-base";
import React, { useEffect, useMemo, useState } from "react";

import Carousel from "@/components/molecules/Carousel";
import Modal from "@/components/molecules/Modal";
import { checkAvailableScheduleDates } from "@/services/api/schedule";
import { formatToWeekDay } from "@/utils/date";

import { DeliveryTimeResult, ModalPropos, VoucherDatails } from "../types";

const EXPRESS_DELIVERY_TIME = 4; //hours

const ScheduleModal = ({
  isOpen,
  currentValue,
  storeId,
  restrictedDays,
  onClose,
  onSubmit,
}: ModalPropos<DeliveryTimeResult> & {
  storeId: string;
  restrictedDays?: VoucherDatails["restrictedDays"];
}) => {
  const currentDate = currentValue?.date;
  const date = new Date();
  const dateStr = moment(date).format("YYYY-MM-DD");
  const hourStr = moment(date).format("HH:mm:ss");
  const requestDate = `${dateStr}T${hourStr}.000Z`;

  const [selectDate, setSelectedDate] = useState(currentDate || date);
  const [selectIndex, setSelectedIndex] = useState(0);

  const { data: scheludeDates } = useQuery({
    queryKey: ["scheldule-dates", storeId],
    queryFn: () => checkAvailableScheduleDates(requestDate, storeId),
    enabled: !!storeId,
  });

  const availableDates = useMemo(() => {
    return scheludeDates
      ?.filter((date) => date.horarios?.length && date?.horarios?.some((h) => h.disponivel))
      .filter((date) =>
        restrictedDays ? restrictedDays.some((rd) => rd.day === date.num_dia) : true
      )
      .map((date) => {
        const restritedDay = restrictedDays?.find((rd) => rd.day === date.num_dia);
        if (restritedDay) {
          return {
            ...date,
            horarios:
              restritedDay.restrictedHours &&
              date.horarios?.filter((hour) =>
                restritedDay.restrictedHours.some((rh) => rh === hour.hora)
              ),
          };
        }
        return date;
      });
  }, [scheludeDates, restrictedDays]);

  useEffect(() => {
    if (!currentDate && availableDates?.length) {
      setSelectedDate(new Date(availableDates[0].data));
      setSelectedIndex(0);
    }
  }, [availableDates, currentDate]);

  const onSelectDate = (date: string, index: number) => {
    setSelectedDate(new Date(date));
    setSelectedIndex(index);
  };

  const selectHour = (hour: number, finalHour: number) => {
    const date = moment(selectDate).set("hour", hour).set("minute", 0).set("second", 0);
    onSubmit({
      date: moment(date).toDate(),
      initialHour: hour,
      finalHour,
    });
    onClose();
  };

  const onSelectExpress = () => {
    onSubmit({
      isExpressDelivery: true,
      expressDeliveryTime: EXPRESS_DELIVERY_TIME,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      Header="Agendar entrega"
      Body={
        <VStack h="600px">
          <Text textAlign="center">
            Selecione uma data e horário dentre os disponíveis para agendar seu pedido
          </Text>
          <VStack py="2">
            <Carousel
              data={availableDates || []}
              itemLayout={20}
              showViewMoreButton={false}
              renderItem={({ item, index }) => (
                <Pressable w="100px" onPress={() => onSelectDate(item.data, index)}>
                  <Box p="2" m="1" justifyContent="center" alignItems="center" rounded="md">
                    <Text color="primary.600" textTransform="capitalize" fontSize="lg">
                      {formatToWeekDay(item.data)}
                    </Text>
                    <Text color="primary.600">{moment(item.data).format("DD/MM")}</Text>
                    {selectDate.getDate() === new Date(item.data).getDate() && (
                      <Divider borderColor="primary.600" borderWidth="2" />
                    )}
                  </Box>
                </Pressable>
              )}
            />
            <VStack>
              <Text fontSize="md" py="4" bold>
                Horários
              </Text>
              <Divider />
              {!!availableDates?.length &&
                selectDate.getDate() === date.getDate() &&
                availableDates[selectIndex]?.entrega_expressa_disponivel && (
                  <Pressable onPress={onSelectExpress}>
                    <HStack py="3" justifyContent="space-between" alignItems="center">
                      <Text color="primary.600" fontSize="md">
                        Entrega expressa - em até {EXPRESS_DELIVERY_TIME}hrs
                      </Text>
                      <Box bgColor="secondary.400" rounded="full" p="1">
                        <Icon
                          as={<MaterialIcons name="arrow-forward-ios" />}
                          color="primary.600"
                          size="md"
                        />
                      </Box>
                    </HStack>
                    <Divider />
                  </Pressable>
                )}
              {availableDates?.length && availableDates[selectIndex]?.horarios?.length ? (
                availableDates[selectIndex]?.horarios?.map((hour) => (
                  <Pressable
                    key={hour?.hora}
                    disabled={!hour.disponivel}
                    onPress={() => selectHour(hour.hora, hour.hora_fim)}>
                    <HStack py="3" justifyContent="space-between" alignItems="center">
                      <Text color={hour.disponivel ? "primary.600" : "gray.400"} fontSize="md">
                        Entre {hour.hora}hrs e {hour.hora_fim}hrs
                      </Text>
                      <Box
                        bgColor={hour.disponivel ? "secondary.400" : "gray.200"}
                        rounded="full"
                        p="1">
                        <Icon
                          as={<MaterialIcons name="arrow-forward-ios" />}
                          color="primary.600"
                          size="md"
                        />
                      </Box>
                    </HStack>
                    <Divider />
                  </Pressable>
                ))
              ) : (
                <Box py="8">
                  <Text textAlign="center" fontSize="lg">
                    Nenhum horário disponível
                  </Text>
                </Box>
              )}
            </VStack>
          </VStack>
        </VStack>
      }
    />
  );
};

export default ScheduleModal;
